import { useState, useRef, useEffect, useCallback, useMemo } from 'react'

interface SearchableSelectProps<T> {
  options: T[]
  value: T | null
  onChange: (value: T | null) => void
  getLabel: (item: T) => string
  getSubLabel?: (item: T) => string
  getId: (item: T) => number | string
  placeholder?: string
  disabled?: boolean
  isLoading?: boolean
  maxVisible?: number
}

export function SearchableSelect<T>({
  options,
  value,
  onChange,
  getLabel,
  getSubLabel,
  getId,
  placeholder = 'Buscar...',
  disabled = false,
  isLoading = false,
  maxVisible = 5,
}: SearchableSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(0)
  const [showAll, setShowAll] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  // Filter options by search text
  const filtered = useMemo(() => {
    if (!search.trim()) return options
    const term = search.toLowerCase()
    return options.filter((item) => {
      const label = getLabel(item).toLowerCase()
      const sub = getSubLabel ? getSubLabel(item).toLowerCase() : ''
      return label.includes(term) || sub.includes(term)
    })
  }, [options, search, getLabel, getSubLabel])

  // Visible options (capped or all)
  const visibleOptions = showAll ? filtered : filtered.slice(0, maxVisible)
  const hiddenCount = filtered.length - maxVisible

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setShowAll(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset highlight when filtered results change
  useEffect(() => {
    setHighlightIndex(0)
  }, [filtered.length, search])

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return
    const items = listRef.current.querySelectorAll('li[data-option]')
    const item = items[highlightIndex] as HTMLElement | undefined
    item?.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex])

  const openDropdown = useCallback(() => {
    if (disabled || isLoading) return
    setIsOpen(true)
    setSearch('')
    setShowAll(false)
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [disabled, isLoading])

  const selectItem = useCallback(
    (item: T) => {
      onChange(item)
      setIsOpen(false)
      setSearch('')
      setShowAll(false)
    },
    [onChange],
  )

  const clearSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(null)
      setSearch('')
    },
    [onChange],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        openDropdown()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightIndex((prev) => Math.min(prev + 1, visibleOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (visibleOptions[highlightIndex]) {
          selectItem(visibleOptions[highlightIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setShowAll(false)
        break
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected value display / trigger */}
      {value && !isOpen ? (
        <div
          onClick={openDropdown}
          className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white flex items-center justify-between cursor-pointer ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <div className="flex-1 min-w-0">
            <span className="block truncate">{getLabel(value)}</span>
            {getSubLabel && (
              <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                {getSubLabel(value)}
              </span>
            )}
          </div>
          {!disabled && (
            <button
              onClick={clearSelection}
              className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
              title="Limpar seleção"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div
          className={`relative ${!isOpen ? 'cursor-pointer' : ''}`}
          onClick={() => !isOpen && openDropdown()}
        >
          {/* Search icon */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            ) : (
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>

          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={openDropdown}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-btn shadow-lg max-h-64 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              Nenhum resultado encontrado
            </div>
          ) : (
            <>
              <ul ref={listRef} className="overflow-y-auto max-h-52">
                {visibleOptions.map((item, index) => (
                  <li
                    key={String(getId(item))}
                    data-option
                    onClick={() => selectItem(item)}
                    onMouseEnter={() => setHighlightIndex(index)}
                    className={`px-4 py-2 cursor-pointer text-sm transition-colors ${
                      index === highlightIndex
                        ? 'bg-primary/10 text-primary dark:text-primary'
                        : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                    } ${value && getId(item) === getId(value) ? 'font-semibold' : ''}`}
                  >
                    <span className="block truncate">{getLabel(item)}</span>
                    {getSubLabel && (
                      <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                        {getSubLabel(item)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Show more button */}
              {!showAll && hiddenCount > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowAll(true)
                  }}
                  className="w-full px-4 py-2 text-xs text-primary hover:bg-primary/5 border-t border-gray-200 dark:border-gray-700 font-medium"
                >
                  Mostrar mais {hiddenCount} {hiddenCount === 1 ? 'resultado' : 'resultados'}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchableSelect
